/* eslint react-hooks/exhaustive-deps: 0 */
import { asArray } from "@/lib/asArray";
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { refLabel } from "@/lib/wsNames";   // Batch 16 · Q10
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const OUTCOMES = ["", "Not likely materially misstated", "Materially misstated"];
const MTYPES = ["", "Factual", "Judgmental", "Projected"];

// M1 — the same 12 audit-only route paths listed in
// backend/w17.py::AUDIT_ONLY_PAGES (grey-not-hide catalog). Any
// engagement-team member can enable one for a matter with a reason;
// the reason is pre-filled `W17.7 matter <ref>` by the backend.
const ENABLE_TARGETS = [
  "/sampling", "/vouching", "/confirmations",
  "/audit-response",
  "/controls/walkthroughs", "/controls/itgc",
  "/controls/a9002", "/controls/deficiency-log",
  "/fieldwork/a9001", "/fieldwork/a9003",
  "/risk",
  "/reporting/auditors-report",
];

// M2 — locale-aware amount format (uses `en-US` for both languages so
// the digits stay Western — matches how W9 renders amounts).
const fmt = (n) => fmtNumber(Number(n || 0));   // STEP 3 · rule 2 · B-2e

function renderMatter(entry, t) {
  const ad = entry.auto_details || {};
  if (entry.auto_kind === "analytical_diff") {
    return t("w17_7Auto.analyticalDiff")
      .replace("{diff}", fmt(ad.difference))
      .replace("{threshold}", fmt(ad.threshold))
      .replace("{fsHead}", ad.fs_head || entry.fs_head || "—");
  }
  if (entry.auto_kind === "inquiry_followup") {
    return t("w17_7Auto.inquiryFollowup")
      .replace("{topic}", t(`w17_4.q.${ad.code}`) || ad.code || "");
  }
  return entry.matter || "";
}

export default function W177RegisterPage() {
  const { current, currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [state, setState] = useState(null);
  const [nm, setNm] = useState({ fs_head: "", matter: "" });
  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/register`, { _silent: true });
    setState(r.data);
  };
  useEffect(() => { reload(); }, [currentEngId]);
  const addManual = async () => {
    if (!nm.matter.trim()) { toast.error(t("w17_7.matterRequired")); return; }
    if (!nm.fs_head.trim()) { toast.error(t("w17_7.fsHeadRequired")); return; }
    try {
      await api.post(`/engagements/${currentEngId}/w17/register/entry`,
                          { ...nm, source: "manual" }, { _silent: true });
      setNm({ fs_head: "", matter: "" }); reload();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  // Batch 28 · Follow-up 1 · F2 (b): the register entries are drafts — one Save / Cancel bar for the register; Save sends only the
  // changed entries, one PATCH each (an outcome "Materially misstated" is sent to Misstatements by that PATCH, as before); a
  // failed entry keeps its draft and its error.
  const td = useTableDraft({
    rows: state ? asArray(state.entries) : [],
    saveRow: async (id, p) => {
      const body = { ...p };
      if ("misstatement_amount" in body) body.misstatement_amount = Number(body.misstatement_amount || 0);
      await api.patch(`/engagements/${currentEngId}/w17/register/${id}`, { patch: body }, { _silent: true });
      if (body.outcome === "Materially misstated") toast.success(t("w17_7.sentToW9"));
    },
    reload,
  });
  const enablePage = async (id, page_id) => {
    try {
      await api.post(`/engagements/${currentEngId}/w17/register/${id}/enable-page`,
                          { page_id }, { _silent: true });
      toast.success(t("w17_7.pageEnabled")); reload();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w17-7-loading">{t("common.loading")}</div>;
  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return <div className="p-6 text-sm text-muted-foreground" data-testid="w17-7-audit-only">{t("w17_7.notForAudit")}</div>;
  return (
    <div className="space-y-6" data-testid="w17-7-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-7-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">507(r)</span>
          {t("w17_7.title")}
        </h1>
        <SignOffBar itemType="w17_7" itemKey="W17.7" saveFirst={td.dirty} />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_7.subtitle")}</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_7.addManual")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select value={nm.fs_head} onChange={e => setNm({...nm, fs_head: e.target.value})}
                     className="h-8 rounded border px-1 text-[11px]"
                     data-testid="w17-7-add-fshead">
              <option value="">{t("w17_7.pickFsHead")}</option>
              {asArray(state.fs_head_options).map(h => <option key={h} value={h}>{h}</option>)}
              <option value="__not_specific__">{t("w17_7Auto.notSpecific")}</option>
            </select>
            <Textarea placeholder={t("w17_7.matterPh")} value={nm.matter} onChange={e => setNm({...nm, matter: e.target.value})} className="h-12 text-[11px]" data-testid="w17-7-add-matter" />
            <Button onClick={addManual} data-testid="w17-7-add-btn">{t("w17_7.add")}</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_7.registerTitle")} · {state.entry_count || 0}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(state.entries || []).length === 0 && (
            <div className="text-[12px] text-muted-foreground" data-testid="w17-7-empty">{t("w17_7.empty")}</div>
          )}
          {asArray(state.entries).map(e => <EntryRow key={e.id} entry={e} draft={td} enablePage={enablePage} t={t} lang={lang} />)}
          <TableSaveBar draft={td} lang={lang} testid="w17-7-table-save" />
        </CardContent>
      </Card>
    </div>
  );
}

function EntryRow({ entry, draft, enablePage, t, lang }) {
  const f = draft.rowValue(entry);
  const set = (k, v) => draft.edit(entry.id, k, v);
  const [pageId, setPageId] = React.useState("");
  const fsHeadLabel = entry.fs_head_not_specific
    ? t("w17_7Auto.notSpecific") : (entry.fs_head || "—");
  return (
    <div className={`rounded border p-2 text-[12px] ${draft.rowClass(entry.id)}`} data-changed={draft.isChanged(entry.id) ? "1" : "0"}
         data-failed={draft.errorOf(entry.id) ? "1" : "0"} data-testid={`w17-7-entry-${entry.id}`}>
      <RowSaveError draft={draft} id={entry.id} testid={`w17-7-entry-error-${entry.id}`} />
      <div className="mb-1 flex items-center justify-between">
        <div className="font-medium" data-testid={`w17-7-entry-${entry.id}-ref`}>
          {refLabel(entry.ref, lang)} · {t(`w17_7.src.${entry.source}`)} · {fsHeadLabel}
        </div>
        {entry.sent_to_misstatement_id && (
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800" data-testid={`w17-7-entry-${entry.id}-w9`}>{t("w17_7.sentToW9Badge")}</span>
        )}
      </div>
      <div className="mb-1" data-testid={`w17-7-entry-${entry.id}-matter`}>{renderMatter(entry, t)}</div>
      <Textarea placeholder={t("w17_7.proceduresPh")} value={f.procedures_designed || ""} onChange={e => set("procedures_designed", e.target.value)} className="h-16 text-[11px]" data-testid={`w17-7-entry-${entry.id}-proc`} />
      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input placeholder={t("w17_7Misstatement.amountLabel")} value={f.misstatement_amount ?? ""}
                onChange={e => set("misstatement_amount", e.target.value)}
                data-testid={`w17-7-entry-${entry.id}-amount`} />
        <select value={f.misstatement_type || ""}
                 onChange={e => set("misstatement_type", e.target.value)}
                 className="h-8 rounded border px-1 text-[11px]"
                 data-testid={`w17-7-entry-${entry.id}-mtype`}>
          {MTYPES.map(m => <option key={m} value={m}>{m ? t(`w9.mtype.${m}`) : t("w17_7Misstatement.pickType")}</option>)}
        </select>
        <select value={f.outcome || ""}
                 onChange={e => set("outcome", e.target.value)}
                 className="h-8 rounded border px-1 text-[11px]"
                 data-testid={`w17-7-entry-${entry.id}-outcome`}>
          {OUTCOMES.map(o => <option key={o} value={o}>{o ? t(`w17_7.outcomeOpt.${o.replace(/\s+/g,"_")}`) : t("w17_7.pickOutcome")}</option>)}
        </select>
      </div>
      {(f.outcome === "Materially misstated"
        && (!(Number(f.misstatement_amount) > 0) || !f.misstatement_type)) && (
        <div className="mt-1 text-[10px] text-amber-800" data-testid={`w17-7-entry-${entry.id}-require-amount`}>
          {t("w17_7Misstatement.requireBeforeSend")}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <select value={pageId} onChange={e => setPageId(e.target.value)}
                 className="h-7 rounded border px-1 text-[11px] flex-1"
                 data-testid={`w17-7-entry-${entry.id}-page`}>
          <option value="">{t("w17_7.pickPageForEnable")}</option>
          {ENABLE_TARGETS.map(p => <option key={p} value={p}>{t(`pageNames.${p}`) || p}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={() => enablePage(entry.id, pageId)} disabled={!pageId.trim()} data-testid={`w17-7-entry-${entry.id}-enable`}>{t("w17_7.enablePage")}</Button>
      </div>
      {(entry.linked_pages || []).length > 0 && (
        <div className="mt-1 text-[10px] text-emerald-800" data-testid={`w17-7-entry-${entry.id}-linked`}>
          {t("w17_7.linkedPages")}: {asArray(entry.linked_pages).map(p => t(`pageNames.${p}`) || p).join(", ")}
        </div>
      )}
    </div>
  );
}
